import { useLayoutEffect, useRef, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import { CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import {
    ImgContainer,
    IMG_CONTAINER_PADDING,
    ImgOverlay,
    ImgOverlayText,
    ErrorContainer,
    Img,
    VideoOverlay,
    StyledOverlayText,
    UnavailableText,
    ClickToPlayText,
    StyledSWReactIcons,
} from "./StyledComponents";

const MIN_IMAGE_HEIGHT = 80;

interface IAdCardImageProps {
    src: string;
    width: number;
    height: number;
    onClick?: () => void;
    isVideo: boolean;
}

export const AdCardImage = ({ src, width, height, onClick, isVideo = false }: IAdCardImageProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [scaledWidth, setScaledWidth] = useState(0);
    const [scaledHeight, setScaledHeight] = useState(0);
    const isFaultyData = width === 0 || height === 0 || !src.length;
    const [hasError, setHasError] = useState(isFaultyData);

    useLayoutEffect(() => {
        const aspectRatio = width / height;
        const newScaledWidth = Math.min(width, containerRef.current?.clientWidth - IMG_CONTAINER_PADDING * 2);
        const newScaledHeight = Math.max(newScaledWidth / aspectRatio, MIN_IMAGE_HEIGHT);

        setScaledWidth(newScaledWidth);
        setScaledHeight(newScaledHeight);
    }, [width, height]);

    return (
        <ImgContainer
            $height={scaledHeight}
            ref={containerRef}
            data-automation={"ad-card-image"}
        >
            {hasError ? (
                <ErrorContainer>
                    {isVideo ? (
                        <>
                            <UnavailableText>
                                ad.intelligence.advertiser.ad.card.video.placeholder.error
                            </UnavailableText>
                            <ClickToPlayText>ad.intelligence.advertiser.ad.card.video.click.to.play</ClickToPlayText>
                            <StyledSWReactIcons
                                iconName="play"
                                size="lg"
                                iconColor={colorsPalettes.carbon[200]}
                            />
                        </>
                    ) : (
                        <SWReactIcons
                            iconName="image"
                            size="md"
                        />
                    )}
                </ErrorContainer>
            ) : !isLoaded ? (
                <PixelPlaceholderLoader
                    height="100%"
                    width={scaledWidth}
                />
            ) : null}

            <Img
                src={src}
                width={width}
                height={height}
                onLoad={() => setIsLoaded(true)}
                onError={() => setHasError(true)}
                $isVisible={isLoaded ? !hasError : null}
            />

            {onClick ? (
                isVideo ? (
                    <VideoOverlay
                        data-automation="video-card-image-overlay"
                        onClick={onClick}
                    >
                        <CenteredFlexRow>
                            <SWReactIcons
                                iconName="play-borderless"
                                size="xl"
                                iconColor={colorsPalettes.carbon[0]}
                            />
                        </CenteredFlexRow>
                        <StyledOverlayText>ad.intelligence.advertiser.ad.card.view.more.info</StyledOverlayText>
                    </VideoOverlay>
                ) : (
                    <ImgOverlay
                        data-automation="ad-card-image-overlay"
                        onClick={onClick}
                    >
                        <ImgOverlayText>ad.intelligence.advertiser.ad.card.view.more.info</ImgOverlayText>
                    </ImgOverlay>
                )
            ) : null}
        </ImgContainer>
    );
};
